import { useEffect, useRef } from 'react';
import lottie, { type AnimationItem } from 'lottie-web';

/**
 * Замена `Lottie.asset` + `AnimationController` из Flutter.
 *
 * Во Flutter анимациями управляет `AnimationController`: он задаёт длительность
 * и гоняет прогресс между границами, а Lottie лишь отрисовывает кадр по этому
 * прогрессу (`Lottie.asset(controller: _controller)`). В lottie-web роль
 * контроллера играет `goToAndStop(progress * totalFrames, true)`, а сам ход
 * времени считает [useAnimationController].
 */

export interface LottieOptions {
  /** Разобранный JSON анимации. */
  readonly animationData: unknown;
  /** Проигрывать самостоятельно, без внешнего контроллера. */
  readonly autoplay?: boolean;
  readonly loop?: boolean;
  /** `BoxFit.contain` по умолчанию, как у `Lottie.asset`. */
  readonly preserveAspectRatio?: string;
}

/** Монтирует анимацию в контейнер и возвращает ссылку на него. */
export function useLottie(
  options: LottieOptions,
): {
  containerRef: React.RefObject<HTMLDivElement | null>;
  animationRef: React.RefObject<AnimationItem | null>;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  const {
    animationData,
    autoplay = false,
    loop = false,
    preserveAspectRatio = 'xMidYMid meet',
  } = options;

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return undefined;

    const animation = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop,
      autoplay,
      animationData: animationData as Record<string, unknown>,
      rendererSettings: { preserveAspectRatio },
    });
    animationRef.current = animation;

    return () => {
      animationRef.current = null;
      animation.destroy();
    };
  }, [animationData, autoplay, loop, preserveAspectRatio]);

  return { containerRef, animationRef };
}

export interface AnimationControllerOptions {
  /** Куда вести прогресс: 1 — вперёд, 0 — назад. */
  readonly forward: boolean;
  /** Сколько занимает полный проход от `lowerBound` до `upperBound`. */
  readonly durationMs: number;
  readonly lowerBound?: number;
  readonly upperBound?: number;
  /** Вызывается, когда прогресс дошёл до цели. */
  readonly onCompleted?: () => void;
  /** Получает прогресс каждого кадра. */
  readonly onTick: (progress: number) => void;
}

/**
 * Перенос `AnimationController.animateTo` на `requestAnimationFrame`.
 *
 * Во Flutter контроллер линейно ведёт значение к цели за заданное время и
 * сообщает `AnimationStatus.completed`. Здесь то же самое: линейная
 * интерполяция по реальному времени кадра, чтобы смена длительности на ходу
 * (микрофон при записи ускоряется в 10 раз) не дёргала картинку.
 */
export function useAnimationController(options: AnimationControllerOptions): void {
  const { forward, durationMs, lowerBound = 0, upperBound = 1, onCompleted, onTick } = options;

  const progressRef = useRef(forward ? lowerBound : upperBound);
  const onTickRef = useRef(onTick);
  const onCompletedRef = useRef(onCompleted);

  onTickRef.current = onTick;
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    const target = forward ? upperBound : lowerBound;
    const span = upperBound - lowerBound;
    // Полная длительность относится ко всему диапазону: если стартуем с
    // середины, оставшийся путь занимает пропорционально меньше времени.
    const distance = Math.abs(target - progressRef.current);
    const remainingMs = span === 0 ? 0 : durationMs * (distance / span);

    if (remainingMs <= 0) {
      progressRef.current = target;
      onTickRef.current(target);
      onCompletedRef.current?.();
      return undefined;
    }

    const from = progressRef.current;
    let startedAt: number | null = null;
    let frame = 0;

    const step = (now: number): void => {
      startedAt ??= now;
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / remainingMs);
      const value = from + (target - from) * t;
      progressRef.current = value;
      onTickRef.current(value);
      if (t < 1) {
        frame = requestAnimationFrame(step);
        return;
      }
      onCompletedRef.current?.();
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [forward, durationMs, lowerBound, upperBound]);
}

/** Ставит кадр анимации по прогрессу 0..1 — как `Lottie(controller:)`. */
export function seekLottie(animation: AnimationItem | null, progress: number): void {
  if (animation === null) return;
  const total = animation.totalFrames;
  if (!Number.isFinite(total) || total <= 0) return;
  animation.goToAndStop(progress * total, true);
}
